import "./FilterStyles.scss";
import { FC, useContext, useEffect, useState } from "react";
import FilterComponentProps from "./FilterComponentProps";
import BrandContext from "../../../../../../../domain/providers/brand/BrandContext";
import BrandContextType from "../../../../../../../domain/providers/brand/BrandContextType";
import ColorContext from "../../../../../../../domain/providers/color/ColorContext";
import ColorContextType from "../../../../../../../domain/providers/color/ColorContextType";
import TypeOfFuelContext from "../../../../../../../domain/providers/typeOfFuel/TypeOfFuelContext";
import TypeOfFuelContextType from "../../../../../../../domain/providers/typeOfFuel/TypeOfFuelContextType";
import TypeVehicleContext from "../../../../../../../domain/providers/typeVehicle/TypeVehicleContext";
import TypeVehicleContextType from "../../../../../../../domain/providers/typeVehicle/TypeVehicleContextType";
import { TraccionCar, TransmissionCar } from "../../../../../../../domain/entities/CarEntity";
import di from "../../../../../../../di/DependencyInjection";
import GetModelsByBrandUseCase, {
    GetModelsByBrandUseCaseName,
} from "../../../../../../../domain/use_cases/brand/GetModelsByBrandUseCase";
import RangePickerComponent from "../../../../../components/form/rangePicker/RangePickerComponent";
import { rangosDePrecioHasta } from "../../../../../components/form/priceRangeChips/PriceRangeChipsComponent";
import PriceRangeChipsComponent from "../../../../../components/form/priceRangeChips/PriceRangeChipsComponent";
import SelectOpenComponent from "../../../../../components/selectOpen/SelectOpenComponent";
import TagContext from "../../../../../../../domain/providers/tag/TagContext";
import TagContextType from "../../../../../../../domain/providers/tag/TagContextType";
import SedeContext from "../../../../../../../domain/providers/sede/SedeContext";
import SedeContextType from "../../../../../../../domain/providers/sede/SedeContextType";
import ModelEntity from "../../../../../../../domain/entities/ModelEntity";
import { useLocation, useNavigate, useRoutes, useSearchParams } from "react-router-dom";

const FilterComponent: FC<FilterComponentProps> = ({
    formFunctions,
    isOpen,
    setIsOpen,
    onChange,
    ocultarTipoVehiculo,
    filtroFijo,
}) => {
    const toggleArrayValue = (current: string[] | undefined, val: string): string[] => {
        const arr = current ?? [];
        return arr.includes(val) ? arr.filter((v) => v !== val) : [...arr, val];
    };

    const { register, setValue, watch } = formFunctions;
    const { brands } = useContext(BrandContext) as BrandContextType;
    const { colors } = useContext(ColorContext) as ColorContextType;
    const { tags } = useContext(TagContext) as TagContextType;
    const { sedes } = useContext(SedeContext) as SedeContextType;
    const { typeOfFuels } = useContext(TypeOfFuelContext) as TypeOfFuelContextType;
    const { typeVehicles } = useContext(TypeVehicleContext) as TypeVehicleContextType;
    const [models, setModels] = useState<ModelEntity[]>([]);
    const years: number[] = (() => {
        const y = watch("year");
        if (!y) return [];
        return String(y)
            .split("-")
            .map(Number)
            .filter((n) => !isNaN(n) && n > 0);
    })();

    const brandIdValue = watch("brand_id");
    const modelValue = watch("model");
    const typeVehicleIds: string[] = watch("type_vehcile_id") ?? [];
    const plateNumberValues: string[] = (() => {
        const v = watch("plate_number");
        if (Array.isArray(v)) return v.map(String);
        return v !== undefined && v !== null && v !== "" ? [String(v)] : [];
    })();
    const typeTransmissions: string[] = watch("type_transmission") ?? [];
    const typeTractions: string[] = watch("traccion") ?? [];
    const availabilityIds: string[] = watch("tag_id") ?? [];
    const typeFuelIds: string[] = watch("type_fuel_id") ?? [];
    const colorIds: string[] = watch("color_id") ?? [];
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const location = useLocation();

    const priceMin = watch("price.min");
    const priceMax = watch("price.max");
    const kmMin = watch("km.min");
    const kmMax = watch("km.max");

    // useEffect(() => {
    //     const url = new URL(window.location.href);
    //     const params = new URLSearchParams(url.search);
    //     setYears(params.get("year")?.split("-"))
    // }, [watch("year")])

    const handleClose = () => {
        if (window.innerWidth < 700) {
            setIsOpen(false);
        }
    };

    const _handleChangeBrand = async (brand: any) => {
        const url = new URL(window.location.href);
        if (brand.id === brandIdValue) {
            setValue("brand_id", undefined);
            url.searchParams.delete("brand");
            url.searchParams.delete("model");
            window.history.replaceState({}, "", url.toString());
        } else {
            setValue("brand_id", brand.id);
            setValue("model", undefined);
            setModels([]);
            url.searchParams.set("brand", brand.name);
            url.searchParams.delete("model");
            // replaceState BEFORE await so queryParams reflects the change on next render
            // and CatalogoPage's useEffect detects the brand change and triggers search
            window.history.replaceState({}, "", url.toString());
            try {
                const response = await di.get<GetModelsByBrandUseCase>(GetModelsByBrandUseCaseName).call(brand.id);
                setModels(response);
            } catch (error) {}
        }
    };

    const _handleChangeModel = (model: any) => {
        const url = new URL(window.location.href);
        if (model === modelValue?.id) {
            setValue("model", undefined);
            url.searchParams.delete("model");
        } else {
            setValue("model", model);
            url.searchParams.set("model", model.name);
        }
        window.history.replaceState({}, "", url.toString());
        handleClose();
    };

    const _handleChangeYear = (year: number) => {
        const isSelected = years.includes(year);
        const newYears = isSelected ? years.filter((y) => y !== year) : [...years, year];
        const yearStr = newYears.join("-") || undefined;

        setValue("year", yearStr);

        const url = new URL(window.location.href);
        if (yearStr) url.searchParams.set("year", yearStr);
        else url.searchParams.delete("year");
        window.history.replaceState({}, "", url.toString());

        handleClose();
    };

    const _handleChangeColor = (color: any) => {
        const newQueryParam = color.name;
        const url = new URL(window.location.href);
        url.searchParams.set("color", newQueryParam);
        window.history.replaceState({}, "", url.toString());
        if (color.name == watch("color_id")) setValue("color_id", undefined);
        else setValue("color_id", color.name);
        handleClose();
    };

    const _handleChangePlateNumber = (plate: number) => {
        const newVal = toggleArrayValue(plateNumberValues, String(plate));
        setValue("plate_number", newVal.length ? newVal : undefined);
        const url = new URL(window.location.href);
        if (newVal.length) url.searchParams.set("plate", newVal.join("-"));
        else url.searchParams.delete("plate");
        window.history.replaceState({}, "", url.toString());
        handleClose();
    };

    useEffect(() => {
        if (
            typeVehicleIds.length ||
            typeTransmissions.length ||
            availabilityIds.length ||
            typeFuelIds.length ||
            typeTractions.length
        ) {
            handleClose();
        }
    }, [
        typeVehicleIds.length,
        typeTransmissions.length,
        availabilityIds.length,
        typeFuelIds.length,
        typeTractions.length,
    ]);

    // Read ALL query params on mount and populate form
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);

        const brandName = params.get("brand");
        if (brandName) {
            const brandFound = brands.find((b) => b.name === brandName);
            if (brandFound) {
                setValue("brand_id", brandFound.id);
            }
            (async () => {
                const modelsData = await di
                    .get<GetModelsByBrandUseCase>(GetModelsByBrandUseCaseName)
                    .call(brandFound?.id);
                setModels(modelsData);
                const modelName = params.get("model");
                if (modelName) {
                    const modelFound = modelsData.find((m) => m.name === modelName);
                    if (modelFound) setValue("model", modelFound);
                }
            })();
        }

        const typeVehicleParam = params.get("type_vehicle");
        if (typeVehicleParam) {
            const parts = typeVehicleParam.split("-").filter(Boolean);
            const allNumeric = parts.every((p) => /^\d+$/.test(p));
            if (allNumeric) {
                setValue("type_vehcile_id", parts);
            } else {
                // Name-based (navbar link) — look up ID by name
                const found = typeVehicles.find((tv) => tv.name === typeVehicleParam);
                if (found) setValue("type_vehcile_id", [String(found.id)]);
            }
        }

        const transmissionParam = params.get("transmission");
        if (transmissionParam) {
            setValue("type_transmission", transmissionParam.split("-").filter(Boolean));
        }

        const traccionParam = params.get("traccion");
        if (traccionParam) {
            setValue("traccion", traccionParam.split("-").filter(Boolean));
        }

        const tagParam = params.get("tag");
        if (tagParam) {
            setValue("tag_id", tagParam.split("-").filter(Boolean));
        }

        const fuelParam = params.get("typeOfFuels");
        if (fuelParam) {
            setValue("type_fuel_id", fuelParam.split("-").filter(Boolean));
        }

        const colorParam = params.get("color");
        if (colorParam) {
            setValue("color_id", colorParam.split("-").filter(Boolean));
        }

        const plateParam = params.get("plate");
        if (plateParam) {
            setValue("plate_number", plateParam.split("-").filter(Boolean));
        }

        const yearParam = params.get("year");
        if (yearParam) setValue("year", yearParam);

        const minPrice = params.get("min_price");
        const maxPrice = params.get("max_price");
        if (minPrice) setValue("price.min", Number(minPrice));
        if (maxPrice) setValue("price.max", Number(maxPrice));

        const kmMinParam = params.get("km_min");
        const kmMaxParam = params.get("km_max");
        if (kmMinParam) setValue("km.min", Number(kmMinParam));
        if (kmMaxParam) setValue("km.max", Number(kmMaxParam));

        const locationParam = params.get("location");
        if (locationParam) {
            setValue("location", locationParam.split("-").filter(Boolean));
        }
    }, []);

    const handleChangeAddQueryParam = (
        value: string,
        propertyForm: any,
        param: string,
        array?: any,
        property?: string,
        index: number = null,
    ) => {
        if (param == "transmission") {
            // value = value == "1" ? "Automática" : "Manual";
            setValue(propertyForm, value);
            const newQueryParam = value;
            const url = new URL(window.location.href);
            url.searchParams.set(param, newQueryParam);
            window.history.replaceState({}, "", url.toString());
            return;
        }
        setValue(propertyForm, value);
        // return;
        if (location.pathname === "/compra-tu-carro/camionetas-usadas") {
            const url = new URL(window.location.href);
            const params = new URLSearchParams(url.search);

            let arrayParams = [];
            for (let [clave, valor] of params.entries()) {
                arrayParams.push(`${clave}=${valor}`);
            }
            const paramsJoin = arrayParams.join("&");
            navigate(`/compra-tu-carro?${paramsJoin}`);
        }

        if (index == 2 && param == "transmission") {
            window.history.replaceState({}, "", new URL(window.location.href).toString());

            navigate(
                "/compra-tu-carro/camionetas-usadas/?type_vehicle=" +
                    (index != null ? array[index][property] : value).toString(),
            );
            setValue("type_vehcile_id", 2);
            return;
        }

        const newQueryParam = index != null ? array[index][property] : value;
        const url = new URL(window.location.href);
        if (newQueryParam === "Híbrido" && param === "type_vehicle") {
            url.searchParams.set("typeOfFuels", newQueryParam);
            window.history.replaceState({}, "", url.toString());
        } else {
            url.searchParams.set(param, newQueryParam);
            window.history.replaceState({}, "", url.toString());
        }
    };

    //useEffect para establecer el filtro de camionetas usadas por defecto en la pagina de camionetas usadas
    useEffect(() => {
        if (location.pathname === "/compra-tu-carro/camionetas-usadas") {
            setValue("type_vehcile_id", ["8"]);
        }
    }, [location]);

    // Sync price range to URL
    useEffect(() => {
        const url = new URL(window.location.href);
        if (priceMin !== undefined && priceMin !== null && priceMin !== "") {
            url.searchParams.set("min_price", String(priceMin));
        } else {
            url.searchParams.delete("min_price");
        }
        if (priceMax !== undefined && priceMax !== null && priceMax !== "") {
            url.searchParams.set("max_price", String(priceMax));
        } else {
            url.searchParams.delete("max_price");
        }
        window.history.replaceState({}, "", url.toString());
    }, [priceMin, priceMax]);

    // Sync km range to URL
    useEffect(() => {
        const url = new URL(window.location.href);
        if (kmMin !== undefined && kmMin !== null && kmMin !== "") {
            url.searchParams.set("km_min", String(kmMin));
        } else {
            url.searchParams.delete("km_min");
        }
        if (kmMax !== undefined && kmMax !== null && kmMax !== "") {
            url.searchParams.set("km_max", String(kmMax));
        } else {
            url.searchParams.delete("km_max");
        }
        window.history.replaceState({}, "", url.toString());
    }, [kmMin, kmMax]);

    return (
        <div className="filter_component_wrapper">
            <div className="filter_submit_bar d-flex justify-content-center">
                <button type="submit" className="btn btn_orange" style={{ width: "90%" }}>
                    Filtrar
                </button>
            </div>
            <div className={`filter_component ps-4 pe-4 ${isOpen && "open"}`}>
                <div className="back_drop" onClick={() => setIsOpen(false)}></div>
                <div className="my-3">
                    <SelectOpenComponent title="Precio">
                        <PriceRangeChipsComponent
                            formFunctions={formFunctions}
                            keyName="price"
                            ranges={rangosDePrecioHasta(filtroFijo?.precioMaximo)}
                            max={filtroFijo?.precioMaximo}
                        />
                    </SelectOpenComponent>
                </div>

                <div className="my-3">
                    <SelectOpenComponent title="Ubicación">
                        <div className="form-check px-0">
                            {sedes.map((sede, index) => {
                                const current: string[] = watch("location") || [];
                                const isChecked = current.includes(String(sede.id));
                                return (
                                    <div key={index} className="form-check my-2">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            value={String(sede.id)}
                                            checked={isChecked}
                                            onChange={() => {
                                                const next = isChecked
                                                    ? current.filter((l) => l !== String(sede.id))
                                                    : [...current, String(sede.id)];
                                                setValue("location", next);
                                                const url = new URL(window.location.href);
                                                if (next.length) url.searchParams.set("location", next.join("-"));
                                                else url.searchParams.delete("location");
                                                window.history.replaceState({}, "", url.toString());
                                                onChange?.();
                                            }}
                                        />
                                        <label className="form-check-label">{sede.name}</label>
                                    </div>
                                );
                            })}
                        </div>
                    </SelectOpenComponent>
                </div>
                <div className="my-3 model_filter">
                    <SelectOpenComponent title="Marca y modelo" search>
                        {(searchTerm: string) =>
                            brands
                                .filter((brand) => brand.name.toLowerCase().includes(searchTerm.toLowerCase()))
                                .map((brand, index) => {
                                    return (
                                        <div
                                            key={index}
                                            className={`form-check my-2 ps-0 ${brandIdValue === brand.id && "active"}`}
                                        >
                                            <div className="my-3">
                                                <div
                                                    className="d-flex align-items-center title hover title"
                                                    onClick={() => _handleChangeBrand(brand)}
                                                >
                                                    <img
                                                        src={brand.image}
                                                        alt="wcar"
                                                        title="wcar"
                                                        className="img-fluid img_filter me-2"
                                                    />
                                                    <span>{brand.name}</span>
                                                </div>
                                                {brandIdValue === brand.id && (
                                                    <div className="content options_box_container">
                                                        {models.map((model, index) => (
                                                            <div
                                                                key={index}
                                                                className={`my-2 option_picker hover ${
                                                                    modelValue?.id == model && "active"
                                                                }`}
                                                                onClick={() => {
                                                                    _handleChangeModel(model);
                                                                }}
                                                            >
                                                                {model.name}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })
                        }
                    </SelectOpenComponent>
                </div>

                <div className="my-3">
                    <SelectOpenComponent title="Año">
                        <div className="options_box_container row">
                            {Array.from(
                                { length: new Date().getFullYear() - (filtroFijo?.anioMinimo ?? 2000) + 1 },
                                (_, index) => (filtroFijo?.anioMinimo ?? 2000) + index,
                            ).map((year, index) => (
                                <div
                                    key={index}
                                    className={`option_picker col-6 col-md-4 col-lg-3 my-2 ${
                                        years?.includes(year) ? "active" : ""
                                    }`}
                                    onClick={() => _handleChangeYear(year)}
                                >
                                    {year}
                                </div>
                            ))}
                        </div>
                    </SelectOpenComponent>
                </div>

                <div className="my-3">
                    <label>Kilometraje</label>
                    <RangePickerComponent formFunctions={formFunctions} keyName="km" min={0} max={1000000} />
                </div>

                {!ocultarTipoVehiculo && (
                    <div className="my-3">
                        <SelectOpenComponent title="Tipo">
                            <div className="form-check px-0">
                                {typeVehicles
                                    .filter(
                                        (typeVehicle) => !filtroFijo?.tiposExcluidos?.includes(String(typeVehicle.id)),
                                    )
                                    .map((typeVehicle, index) => (
                                        <div key={index} className="form-check my-2">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                value={typeVehicle.id}
                                                checked={typeVehicleIds.includes(String(typeVehicle.id))}
                                                onChange={(e) => {
                                                    if (e.target.value === "8" && !filtroFijo) {
                                                        const url = new URL(window.location.href);
                                                        navigate(
                                                            `/compra-tu-carro/camionetas-usadas?${url.searchParams.toString()}`,
                                                        );
                                                    }
                                                    const newVal = toggleArrayValue(
                                                        typeVehicleIds,
                                                        String(typeVehicle.id),
                                                    );
                                                    setValue("type_vehcile_id", newVal);
                                                    const url = new URL(window.location.href);
                                                    if (newVal.length)
                                                        url.searchParams.set("type_vehicle", newVal.join("-"));
                                                    else url.searchParams.delete("type_vehicle");
                                                    window.history.replaceState({}, "", url.toString());
                                                }}
                                            />
                                            <label className="form-check-label">
                                                <img
                                                    src={typeVehicle.photo}
                                                    alt={typeVehicle.name}
                                                    width={25}
                                                    className="me-2"
                                                />
                                                {typeVehicle.name}{" "}
                                                <span className="text_gray">({typeVehicle.count ?? 0})</span>
                                            </label>
                                        </div>
                                    ))}
                            </div>
                        </SelectOpenComponent>
                    </div>
                )}

                <div className="my-3">
                    <SelectOpenComponent title="Transmisión">
                        <div className="form-check px-0">
                            {[
                                { label: "Automática", value: TransmissionCar.AUTOMATIC },
                                { label: "Manual", value: TransmissionCar.MANUAL },
                            ].map((item, index) => (
                                <div key={index} className="form-check my-2">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        checked={typeTransmissions.includes(item.value)}
                                        value={item.value}
                                        onChange={() => {
                                            const newVal = toggleArrayValue(typeTransmissions, item.value);
                                            setValue("type_transmission", newVal);
                                            const url = new URL(window.location.href);
                                            if (newVal.length) url.searchParams.set("transmission", newVal.join("-"));
                                            else url.searchParams.delete("transmission");
                                            window.history.replaceState({}, "", url.toString());
                                        }}
                                    />
                                    <label className="form-check-label">{item.label}</label>
                                </div>
                            ))}
                        </div>
                    </SelectOpenComponent>
                </div>

                <div className="my-3">
                    <SelectOpenComponent title="Tracción">
                        <div className="form-check px-0">
                            {[
                                { label: "Delantera", value: String(TraccionCar.DELANTERA) },
                                { label: "Trasera", value: String(TraccionCar.TRASERA) },
                                { label: "4 x 4", value: String(TraccionCar.FOURXFOUR) },
                                { label: "4 x 2", value: String(TraccionCar.FOURXTWO) },
                            ].map((item, index) => (
                                <div key={index} className="form-check my-2">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        checked={typeTractions.includes(item.value)}
                                        value={item.value}
                                        onChange={() => {
                                            const newVal = toggleArrayValue(typeTractions, item.value);
                                            setValue("traccion", newVal);
                                            const url = new URL(window.location.href);
                                            if (newVal.length) url.searchParams.set("traccion", newVal.join("-"));
                                            else url.searchParams.delete("traccion");
                                            window.history.replaceState({}, "", url.toString());
                                        }}
                                    />
                                    <label className="form-check-label">{item.label}</label>
                                </div>
                            ))}
                        </div>
                    </SelectOpenComponent>
                </div>

                <div className="my-3">
                    <SelectOpenComponent title="Disponibilidad">
                        <div className="form-check px-0">
                            {tags
                                .filter((tag) => tag.id != "9" && tag.id != "13")
                                .map((tag, index) => (
                                    <div key={index} className="form-check my-2">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            value={tag.id}
                                            checked={availabilityIds.includes(String(tag.id))}
                                            onChange={() => {
                                                const newVal = toggleArrayValue(availabilityIds, String(tag.id));
                                                setValue("tag_id", newVal);
                                                const url = new URL(window.location.href);
                                                if (newVal.length) url.searchParams.set("tag", newVal.join("-"));
                                                else url.searchParams.delete("tag");
                                                window.history.replaceState({}, "", url.toString());
                                            }}
                                        />
                                        <label className="form-check-label">{tag.name}</label>
                                    </div>
                                ))}
                        </div>
                    </SelectOpenComponent>
                </div>

                <div className="my-3">
                    <SelectOpenComponent title="Combustible">
                        <div className="form-check px-0">
                            {typeOfFuels.map((typeOfFuel, index) => (
                                <div key={index} className="form-check my-2">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        value={typeOfFuel.id}
                                        checked={typeFuelIds.includes(String(typeOfFuel.id))}
                                        onChange={() => {
                                            const newVal = toggleArrayValue(typeFuelIds, String(typeOfFuel.id));
                                            setValue("type_fuel_id", newVal);
                                            const url = new URL(window.location.href);
                                            if (newVal.length) url.searchParams.set("typeOfFuels", newVal.join("-"));
                                            else url.searchParams.delete("typeOfFuels");
                                            window.history.replaceState({}, "", url.toString());
                                        }}
                                    />
                                    <label className="form-check-label">{typeOfFuel.name}</label>
                                </div>
                            ))}
                        </div>
                    </SelectOpenComponent>
                </div>

                <div className="my-3 color_filter">
                    <SelectOpenComponent title="Color">
                        <div className="colors_container">
                            {colors.map((color, index) => (
                                <div
                                    key={index}
                                    className={`color ${colorIds.includes(color.name) ? "active" : ""}`}
                                    onClick={() => {
                                        const newVal = toggleArrayValue(colorIds, color.name);
                                        setValue("color_id", newVal);
                                        const url = new URL(window.location.href);
                                        if (newVal.length) url.searchParams.set("color", newVal.join("-"));
                                        else url.searchParams.delete("color");
                                        window.history.replaceState({}, "", url.toString());
                                    }}
                                >
                                    <div className="fill" style={{ backgroundColor: color.colorHex }}></div>
                                </div>
                            ))}
                        </div>
                    </SelectOpenComponent>
                </div>

                <div className="my-3">
                    <SelectOpenComponent title="Placa">
                        <div className="options_box_container">
                            {Array.from({ length: 10 }, (_, index) => (
                                <div
                                    key={index}
                                    className={`option_picker hover ${plateNumberValues.includes(String(index)) ? "active" : ""}`}
                                    onClick={() => {
                                        _handleChangePlateNumber(index);
                                    }}
                                >
                                    <span>{index}</span>
                                </div>
                            ))}
                        </div>
                    </SelectOpenComponent>
                </div>
            </div>
        </div>
    );
};

FilterComponent.defaultProps = {
    isOpen: false,
};
export default FilterComponent;
